import React, { useState, useRef, useEffect, useMemo } from 'react';

// Searchable employee picker — type to filter, or open the list and pick.
// Drop-in replacement for a <select> bound to an employee id (UUID string).
//   value     currently selected employee id ('' for none)
//   onChange  called with the chosen employee id
const inputCls =
  'w-full rounded-md border-gray-200 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100 text-[14px] focus:border-brand-500 focus:ring-brand-500';

function labelFor(emp) {
  if (!emp) return '';
  const title = emp.job_title ? ` — ${emp.job_title}` : '';
  const pending = emp.status === 'pending_start' ? ' (New starter — not yet submitted)' : '';
  return `${emp.first_name} ${emp.last_name}${title}${pending}`;
}

export default function EmployeeCombobox({
  employees = [],
  value,
  onChange,
  id,
  placeholder = 'Search or select an employee…',
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const wrapRef = useRef(null);
  const listboxId = id ? `${id}-listbox` : 'employee-combobox-listbox';

  const selectedEmp = useMemo(
    () => employees.find((e) => e.id === value) || null,
    [employees, value],
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return employees;
    return employees.filter((e) =>
      `${e.first_name} ${e.last_name} ${e.job_title || ''}`.toLowerCase().includes(q),
    );
  }, [employees, query]);

  // While open the input reflects what's being typed; while closed it shows the selection.
  const inputValue = open ? query : labelFor(selectedEmp);

  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  const choose = (emp) => {
    onChange(emp.id);
    setQuery('');
    setOpen(false);
  };

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!open) { setOpen(true); return; }
      setActiveIndex((i) => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      if (open && filtered[activeIndex]) {
        e.preventDefault();
        choose(filtered[activeIndex]);
      }
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  return (
    <div ref={wrapRef} className="relative">
      <input
        id={id}
        type="text"
        role="combobox"
        aria-expanded={open}
        aria-controls={listboxId}
        aria-autocomplete="list"
        autoComplete="off"
        className={inputCls}
        placeholder={placeholder}
        value={inputValue}
        onChange={(e) => { setQuery(e.target.value); setOpen(true); setActiveIndex(0); }}
        onFocus={() => { setOpen(true); setQuery(''); }}
        onKeyDown={onKeyDown}
      />
      {open && (
        <ul
          id={listboxId}
          role="listbox"
          className="absolute z-20 mt-1 max-h-64 w-full overflow-auto rounded-md bg-white dark:bg-gray-800 ring-1 ring-gray-200 dark:ring-gray-700 shadow-lg py-1"
        >
          {filtered.length === 0 ? (
            <li className="px-3 py-2 text-[13px] text-gray-500">No matching employees</li>
          ) : (
            filtered.map((emp, i) => (
              <li
                key={emp.id}
                role="option"
                aria-selected={emp.id === value}
                onMouseDown={(e) => { e.preventDefault(); choose(emp); }}
                onMouseEnter={() => setActiveIndex(i)}
                className={`px-3 py-2 text-[13.5px] cursor-pointer ${
                  i === activeIndex ? 'bg-brand-50 dark:bg-brand-900/30' : ''
                } ${emp.id === value ? 'font-semibold text-brand-800 dark:text-brand-200' : 'text-ink dark:text-gray-100'}`}
              >
                {emp.first_name} {emp.last_name}
                {emp.job_title && <span className="text-gray-500"> — {emp.job_title}</span>}
                {emp.status === 'pending_start' && (
                  <span className="text-amber-600"> (New starter — not yet submitted)</span>
                )}
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
}
